import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Destination, LoadingState } from '../../types';
import { destinationService } from '../../services/destinationService';

interface DestinationState extends LoadingState {
  destinations: Destination[];
  selectedDestination: Destination | null;
  // Public paginated destinations
  publicDestinations: Destination[];
  publicPagination: any;
  isLoadingMore: boolean;
  // Admin state
  adminDestinations: Destination[];
  pagination: any;
  isCreating: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
}

const initialState: DestinationState = {
  destinations: [],
  selectedDestination: null,
  isLoading: false,
  error: null,
  // Public paginated destinations
  publicDestinations: [],
  publicPagination: null,
  isLoadingMore: false,
  // Admin state
  adminDestinations: [],
  pagination: null,
  isCreating: false,
  isUpdating: false,
  isDeleting: false,
};

// Async Thunks
export const fetchDestinations = createAsyncThunk(
  'destinations/fetchDestinations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await destinationService.getDestinations();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch destinations');
    }
  }
);

export const fetchPaginatedDestinations = createAsyncThunk(
  'destinations/fetchPaginatedDestinations',
  async (params: { page?: number; limit?: number; loadMore?: boolean } = {}, { rejectWithValue }) => {
    try {
      const response = await destinationService.getPaginatedDestinations(params);
      return { ...response.data, loadMore: params.loadMore };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch destinations');
    }
  }
);

export const fetchDestinationById = createAsyncThunk(
  'destinations/fetchDestinationById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await destinationService.getDestinationById(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch destination');
    }
  }
);

// Admin CRUD Operations
export const fetchAllDestinations = createAsyncThunk(
  'destinations/fetchAllDestinations',
  async (params: { page?: number; limit?: number } = {}, { rejectWithValue }) => {
    try {
      const response = await destinationService.getAllDestinations(params);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch destinations');
    }
  }
);

export const createDestination = createAsyncThunk(
  'destinations/createDestination',
  async (destinationData: { name: string; details: string; isPopular: boolean; media: string }, { rejectWithValue }) => {
    try {
      const response = await destinationService.createDestination(destinationData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create destination');
    }
  }
);

export const updateDestination = createAsyncThunk(
  'destinations/updateDestination',
  async ({ id, data }: { id: string; data: { name: string; details: string; isPopular: boolean; media: string } }, { rejectWithValue }) => {
    try {
      const response = await destinationService.updateDestination(id, data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update destination');
    }
  }
);

export const deleteDestination = createAsyncThunk(
  'destinations/deleteDestination',
  async (id: string, { rejectWithValue }) => {
    try {
      await destinationService.deleteDestination(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete destination');
    }
  }
);

export const togglePopularStatus = createAsyncThunk(
  'destinations/togglePopularStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await destinationService.togglePopularStatus(id);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to toggle popular status');
    }
  }
);

const destinationSlice = createSlice({
  name: 'destinations',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setSelectedDestination: (state, action: PayloadAction<Destination | null>) => {
      state.selectedDestination = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Destinations
      .addCase(fetchDestinations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDestinations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.destinations = action.payload;
      })
      .addCase(fetchDestinations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Destination by ID
      .addCase(fetchDestinationById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchDestinationById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.selectedDestination = action.payload;
      })
      .addCase(fetchDestinationById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Fetch Paginated Destinations (Public)
      .addCase(fetchPaginatedDestinations.pending, (state, action) => {
        if (action.meta.arg.loadMore) {
          state.isLoadingMore = true;
        } else {
          state.isLoading = true;
        }
        state.error = null;
      })
      .addCase(fetchPaginatedDestinations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        if (action.payload.loadMore) {
          // Append to existing destinations for load more
          state.publicDestinations = [...state.publicDestinations, ...action.payload.data];
        } else {
          // Replace destinations for initial load
          state.publicDestinations = action.payload.data;
        }
        state.publicPagination = action.payload.pagination;
      })
      .addCase(fetchPaginatedDestinations.rejected, (state, action) => {
        state.isLoading = false;
        state.isLoadingMore = false;
        state.error = action.payload as string;
      })
      // Fetch All Destinations (Admin)
      .addCase(fetchAllDestinations.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDestinations.fulfilled, (state, action) => {
        state.isLoading = false;
        state.adminDestinations = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchAllDestinations.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Create Destination
      .addCase(createDestination.pending, (state) => {
        state.isCreating = true;
        state.error = null;
      })
      .addCase(createDestination.fulfilled, (state, action) => {
        state.isCreating = false;
        state.adminDestinations.unshift(action.payload);
      })
      .addCase(createDestination.rejected, (state, action) => {
        state.isCreating = false;
        state.error = action.payload as string;
      })
      // Update Destination
      .addCase(updateDestination.pending, (state) => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(updateDestination.fulfilled, (state, action) => {
        state.isUpdating = false;
        const index = state.adminDestinations.findIndex(dest => dest.id === action.payload.id);
        if (index !== -1) {
          state.adminDestinations[index] = action.payload;
        }
      })
      .addCase(updateDestination.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })
      // Delete Destination
      .addCase(deleteDestination.pending, (state) => {
        state.isDeleting = true;
        state.error = null;
      })
      .addCase(deleteDestination.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.adminDestinations = state.adminDestinations.filter(dest => dest.id !== action.payload);
      })
      .addCase(deleteDestination.rejected, (state, action) => {
        state.isDeleting = false;
        state.error = action.payload as string;
      })
      // Toggle Popular Status
      .addCase(togglePopularStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(togglePopularStatus.fulfilled, (state, action) => {
        const index = state.adminDestinations.findIndex(dest => dest.id === action.payload.id);
        if (index !== -1) {
          state.adminDestinations[index] = action.payload;
        }
      })
      .addCase(togglePopularStatus.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setSelectedDestination } = destinationSlice.actions;
export default destinationSlice.reducer;
